import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TableModule } from 'primeng/table';
import { AdminPageHeaderComponent } from '../../shared/admin-page-header/admin-page-header.component';
import { League, LeagueParticipant, LeagueParticipantDetail } from '../../../models/league';
import { LeagueService } from '../../../services/league.service';

type ParticipantRound = LeagueParticipantDetail['rounds'][number];
type ParticipantMatch = ParticipantRound['all_matches'][number];
type MatchMetricKey =
  | 'duration'
  | 'score'
  | 'won'
  | 'kda'
  | 'death_zero_bonus'
  | 'turrets'
  | 'gold_score'
  | 'vision_score'
  | 'baron'
  | 'elder_dragon'
  | 'elemental_dragon'
  | 'herald'
  | 'horde';

@Component({
  selector: 'app-league-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TableModule,
    DatePipe,
    DecimalPipe,
    AdminPageHeaderComponent,
  ],
  templateUrl: './league-detail.component.html',
  styleUrls: ['./league-detail.component.scss'],
})
export class LeagueDetailComponent implements OnInit {
  title = 'Detalhe da Liga';
  subtitle = 'Acompanhe rounds, participantes e histórico operacional por jogador.';
  leagueId = 0;

  league: League | null = null;
  participants: LeagueParticipant[] = [];
  expandedParticipantId: number | null = null;
  participantDetailsById: Record<number, LeagueParticipantDetail> = {};
  participantDetailErrorsById: Record<number, string> = {};
  loadingParticipantDetailId: number | null = null;
  participantSearch = '';
  participantIdToAdd: number | null = null;

  isLoading = false;
  isLoadingParticipants = false;
  participantsPage = 1;
  participantsTotal = 0;
  participantsPageSize = 20;
  feedbackError = '';
  feedbackSuccess = '';
  readonly matchMetrics: Array<{ key: MatchMetricKey; label: string }> = [
    { key: 'duration', label: 'Duração (MM:SS)' },
    { key: 'score', label: 'Score' },
    { key: 'won', label: 'Vitória' },
    { key: 'kda', label: 'KDA' },
    { key: 'death_zero_bonus', label: 'Zero Mortes' },
    { key: 'turrets', label: 'Torres' },
    { key: 'gold_score', label: 'Ouro' },
    { key: 'vision_score', label: 'Visão' },
    { key: 'baron', label: 'Baron' },
    { key: 'elder_dragon', label: 'Dragão Ancião' },
    { key: 'elemental_dragon', label: 'Dragão Elemental' },
    { key: 'herald', label: 'Arauto' },
    { key: 'horde', label: 'Vastilarva' },
  ];

  constructor(private route: ActivatedRoute, private leagueService: LeagueService) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    this.leagueId = Number(idParam);
    this.loadLeague();
    this.loadParticipants();
  }

  loadLeague(): void {
    this.isLoading = true;
    this.leagueService.get(this.leagueId).subscribe({
      next: (league) => {
        this.league = league;
        this.title = `Liga ${league.name}`;
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível carregar a liga.');
      },
    });
  }

  loadParticipants(): void {
    this.isLoadingParticipants = true;
    this.leagueService
      .listParticipants(this.leagueId, {
        search: this.participantSearch,
        page: this.participantsPage,
        page_size: this.participantsPageSize,
      })
      .subscribe({
        next: (response) => {
          this.participants = response.results || [];
          this.participantsTotal = response.count || 0;
          this.isLoadingParticipants = false;
        },
        error: (error) => {
          this.participants = [];
          this.participantsTotal = 0;
          this.isLoadingParticipants = false;
          this.feedbackError = this.extractErrorMessage(error, 'Não foi possível carregar participantes.');
        },
      });
  }

  filterParticipants(): void {
    this.participantsPage = 1;
    this.loadParticipants();
  }

  addParticipant(): void {
    if (!this.participantIdToAdd) {
      this.feedbackError = 'Informe o ID do jogador para adicionar.';
      return;
    }
    this.feedbackError = '';
    this.feedbackSuccess = '';
    this.leagueService.addParticipant(this.leagueId, this.participantIdToAdd).subscribe({
      next: () => {
        this.participantIdToAdd = null;
        this.feedbackSuccess = 'Participante adicionado com sucesso.';
        this.loadLeague();
        this.loadParticipants();
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível adicionar participante.');
      },
    });
  }

  removeParticipant(playerId: number): void {
    this.feedbackError = '';
    this.feedbackSuccess = '';
    this.leagueService.removeParticipant(this.leagueId, playerId).subscribe({
      next: () => {
        this.feedbackSuccess = 'Participante removido com sucesso.';
        this.loadLeague();
        this.loadParticipants();
        delete this.participantDetailsById[playerId];
        delete this.participantDetailErrorsById[playerId];
        if (this.expandedParticipantId === playerId) {
          this.expandedParticipantId = null;
        }
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível remover participante.');
      },
    });
  }

  toggleParticipantDetail(playerId: number): void {
    if (this.expandedParticipantId === playerId) {
      this.expandedParticipantId = null;
      return;
    }

    this.expandedParticipantId = playerId;
    if (this.participantDetailsById[playerId] || this.loadingParticipantDetailId === playerId) {
      return;
    }

    delete this.participantDetailErrorsById[playerId];
    this.loadingParticipantDetailId = playerId;
    this.feedbackError = '';

    this.leagueService.getParticipantDetail(this.leagueId, playerId).subscribe({
      next: (detail) => {
        this.participantDetailsById[playerId] = detail;
        if (this.loadingParticipantDetailId === playerId) {
          this.loadingParticipantDetailId = null;
        }
      },
      error: (error) => {
        const message = this.extractErrorMessage(error, 'Não foi possível carregar o detalhe do participante.');
        this.participantDetailErrorsById[playerId] = message;
        this.feedbackError = message;
        if (this.loadingParticipantDetailId === playerId) {
          this.loadingParticipantDetailId = null;
        }
      },
    });
  }

  isParticipantExpanded(playerId: number): boolean {
    return this.expandedParticipantId === playerId;
  }

  isParticipantDetailLoading(playerId: number): boolean {
    return this.loadingParticipantDetailId === playerId;
  }

  getParticipantDetail(playerId: number): LeagueParticipantDetail | null {
    return this.participantDetailsById[playerId] || null;
  }

  getParticipantDetailError(playerId: number): string {
    return this.participantDetailErrorsById[playerId] || '';
  }

  getSortedRoundMatches(round: ParticipantRound): ParticipantMatch[] {
    return [...(round.all_matches || [])].sort((a, b) => this.toNumber(b.score) - this.toNumber(a.score));
  }

  getMatchMetricValue(match: ParticipantMatch, metric: MatchMetricKey): string {
    if (metric === 'duration') {
      return this.formatDuration(match.game_duration);
    }

    return this.formatNumericValue(match[metric]);
  }

  private formatDuration(value: number | string | null): string {
    const totalSeconds = Math.max(0, Math.floor(this.toNumber(value)));
    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, '0');
    const seconds = Math.floor(totalSeconds % 60)
      .toString()
      .padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  private formatNumericValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '-';
    }

    const numeric = this.toNumber(value);
    if (Number.isInteger(numeric)) {
      return `${numeric}`;
    }

    return numeric.toFixed(2);
  }

  private toNumber(value: unknown): number {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  }

  onLazyLoad(event: { first?: number }): void {
    const first = event.first ?? 0;
    const page = Math.floor(first / this.participantsPageSize) + 1;
    if (page !== this.participantsPage) {
      this.participantsPage = page;
      this.loadParticipants();
    }
  }

  private extractErrorMessage(error: any, fallback: string): string {
    const response = error?.error;
    if (!response) {
      return fallback;
    }

    if (typeof response === 'string') {
      return response;
    }

    if (typeof response?.detail === 'string') {
      return response.detail;
    }

    const fieldMessages: string[] = [];
    Object.entries(response).forEach(([field, value]) => {
      if (Array.isArray(value)) {
        fieldMessages.push(`${field}: ${value.join(', ')}`);
      } else if (typeof value === 'string') {
        fieldMessages.push(`${field}: ${value}`);
      }
    });

    if (fieldMessages.length > 0) {
      return fieldMessages.join(' | ');
    }

    return fallback;
  }
}
